// frontend/src/pages/SystemAdminAccess.jsx
import React, { useEffect, useState, useMemo } from "react";
import Page from "../ui/Page";

export default function SystemAdminAccess() {
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("/api/auth?action=me-user", { credentials: "include" });
        const j = await r.json().catch(() => ({}));
        const adminFlag = !!(j && (j.admin === true || j.is_admin === true || j.role === "admin"));
        setIsAdmin(adminFlag);
      } catch {
        setIsAdmin(false);
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  return (
    <Page>
      <main className="menu-wrap">
        <h1 className="menu-title">System Administration — Access Approvals</h1>

        {checking && (
          <div className="box" style={{ marginTop: 12 }}>
            Đang kiểm tra quyền / Checking permissions…
          </div>
        )}

        {!checking && !isAdmin && (
          <div className="box" style={{ marginTop: 12 }}>
            <strong>403 — Forbidden.</strong> Bạn không có quyền truy cập trang này.
            <div style={{ marginTop: 8 }}>
              <a className="inline" href="/menu">← Back to Menu</a>
            </div>
          </div>
        )}

        {!checking && isAdmin && (
          <>
            <div className="box" style={{ marginTop: 12 }}>
              <p>Approve registration requests and send/resend OTP codes.</p>
            </div>
            <div className="login-wrap wide" style={{ paddingTop: 0 }}>
              <AdminPanel />
            </div>
          </>
        )}
      </main>
    </Page>
  );
}

function AdminPanel() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [token, setToken] = useState("");
  const [pending, setPending] = useState([]);
  const [awaiting, setAwaiting] = useState([]); // approved but not yet verified
  const [note, setNote] = useState("");

  const headers = useMemo(
    () =>
      token
        ? { "X-ADMIN-TOKEN": token, "Content-Type": "application/json" }
        : { "Content-Type": "application/json" },
    [token]
  );

  async function call(action, payload) {
    const res = await fetch(`/api/auth`, {
      method: "POST",
      headers,
      body: JSON.stringify({ action, ...payload }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.ok) throw new Error(data.error || res.statusText);
    return data;
  }

  async function load() {
    setLoading(true);
    setError("");
    setInfo("");
    try {
      const [p, a] = await Promise.all([
        call("admin-list-pending"),
        call("admin-list-awaiting-verify"),
      ]);
      setPending(p.items || []);
      setAwaiting(a.items || []);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function approve(email) {
    setLoading(true);
    setError("");
    setInfo("");
    try {
      await call("admin-approve", { email, note });
      await call("send-otp", { email });
      setInfo(`Approved and OTP sent to ${email}`);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
      await load();
    }
  }

  async function sendOtp(email) {
    setLoading(true);
    setError("");
    setInfo("");
    try {
      await call("send-otp", { email });
      setInfo("OTP sent to " + email);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <style>{`
        .section { width: 100%; max-width: 640px; margin: 0 auto; }
        @media (min-width: 1024px) { .section { max-width: 720px; } }
        .field { margin-top: 14px; width: 100%; max-width: 520px; text-align: left; margin-left: auto; margin-right: auto; }
        .label { display:block; font-size: 14px; font-weight: 600; margin-bottom: 6px; }
        .input { width:100%; box-sizing:border-box; border:1px solid #cbd5e1; border-radius:12px; padding:12px 14px; font-size:16px; background:#ffffff; color:#111827; }
        .btn { width:100%; margin: 12px auto 0; border-radius: 12px; padding: 12px 16px; font-weight:700; border:1px solid #94a3b8; background:#f8fafc; max-width: 520px; display: block; }
        .table { border-collapse: collapse; width: 100%; margin-top: 16px; }
        .table th, .table td { border: 1px solid #e5e7eb; padding: 8px; text-align: left; }
        .links { display:flex; flex-wrap: wrap; justify-content: flex-start; gap:12px; margin-top:20px; font-size: 14px; }
      `}</style>

      {error && <div className="err" style={{ color: "#dc2626", marginTop: 8 }}>{error}</div>}
      {info && <div className="success" style={{ color: "#16a34a", marginTop: 8 }}>{info}</div>}

      <div className="section">
        <div className="field">
          <label className="label">(Tùy chọn) Admin token / Optional admin token</label>
          <input
            className="input"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Leave empty if using admin session"
          />
        </div>
        <div className="field">
          <label className="label">Ghi chú / Note</label>
          <input
            className="input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note when approving"
          />
        </div>
        <div className="field" style={{ maxWidth: 520 }}>
          <button className="btn" onClick={load} disabled={loading}>
            {loading ? "Đang tải…" : "Refresh"}
          </button>
        </div>
      </div>

      <h3 style={{ marginTop: 16 }}>Pending</h3>
      <table className="table">
        <thead>
          <tr>
            <th>Email</th><th>Status</th><th>Created</th><th>Approved</th><th>By</th><th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {pending.length === 0 && (
            <tr><td colSpan="6">No pending requests</td></tr>
          )}
          {pending.map((r) => (
            <tr key={r.email}>
              <td>{r.email}</td>
              <td>{r.status}</td>
              <td>{r.created_at}</td>
              <td>{r.approved_at || "-"}</td>
              <td>{r.approved_by || "-"}</td>
              <td>
                <div className="section" style={{ maxWidth: 520, padding: 0 }}>
                  <button className="btn" onClick={() => approve(r.email)} disabled={loading}>
                    Approve &amp; Send OTP
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 style={{ marginTop: 16 }}>Approved — awaiting verification</h3>
      <table className="table">
        <thead>
          <tr>
            <th>Email</th><th>Status</th><th>Approved</th><th>By</th><th>Last Created</th><th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {awaiting.length === 0 && (
            <tr><td colSpan="6">Không có mục nào / Empty</td></tr>
          )}
          {awaiting.map((r) => (
            <tr key={r.email}>
              <td>{r.email}</td>
              <td>{r.status}</td>
              <td>{r.approved_at || "-"}</td>
              <td>{r.approved_by || "-"}</td>
              <td>{r.created_at}</td>
              <td>
                <div className="section" style={{ maxWidth: 520, padding: 0 }}>
                  <button className="btn" onClick={() => sendOtp(r.email)} disabled={loading}>
                    Resend OTP
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="links" style={{ marginTop: 16 }}>
        <a href="/sysadmin">← Back to System Admin</a>
      </div>
    </div>
  );
}
